import React, { ChangeEvent } from "react";

import {
  IconPosition,
  Select,
  SelectOptions,
  Toggle,
  ToggleOptionProps,
} from "@at/design-system-elements";
import { TypeInsignia } from "@at/design-system-foundation";
import { IconReset } from "@at/design-system-icons";
import { Banner } from "@at/design-system-patterns";
import {
  getChannelSingular,
  dataAttrGUI,
  dataAttrTestId,
} from "@at/formatter-utils";
import { asFormattedString } from "@at/formatter-utils/lib/number-utils/number.utils";
import { useQueryParameters } from "@at/hooks";
import { constructLeaseOption } from "@at/leasing-utils/lib/leasing-utils/lease-option-utils";

import { setLeaseOption } from "../../../../../contexts/leasing/actions/leasing.actions";
import { useLeasingDispatchContext } from "../../../../../contexts/leasing/context/leasing.context";
import { useChannel } from "../../../../../utils/hooks/use-channel";
import { calculateMonthToYear } from "../../../../../utils/leasing/calculator-utils";
import {
  CONTRACT_LENGTH,
  INITIAL_PAYMENT_MULTIPLIER,
  MILEAGE_OPTIONS,
} from "../../../../../utils/leasing/state/state";
import { useTracking } from "../../../../../utils/tracking/use-tracking";
import { getLeasingConfigEntity } from "../../../../../utils/tracking-util";

import { useLeasingData } from "../../hooks/useLeasingData/use-leasing-data.hook";
import * as Styled from "../../leasing-contract-customiser.styled";

export const CustomiseYourPayment = () => {
  const { state, deposit, leaseTermParts } = useLeasingData();
  const dispatch = useLeasingDispatchContext();
  const { leaseOffersData } = state;
  const { trackEvent } = useTracking();
  const leasingEntity = getLeasingConfigEntity(state);
  const channel = useChannel();
  const channelSingular = getChannelSingular(channel);
  const { setQueryParamsAndHistory } = useQueryParameters();
  const leaseOffersAvailable = !!leaseOffersData?.length;

  const handleQueryParams = (leaseTermParts: string) => {
    setQueryParamsAndHistory(
      {
        leaseOption: leaseTermParts,
        lease_option: "",
      },
      true,
    );
  };

  const handleAnnualMileageSelection = (
    event: ChangeEvent<HTMLSelectElement>,
  ) => {
    const selectedValue = event.target.value;
    const amendedLeaseOption = leaseTermParts
      ? constructLeaseOption({
          ...leaseTermParts,
          mileage: Number(selectedValue || 0),
        })
      : "";
    dispatch(setLeaseOption(amendedLeaseOption));
    handleQueryParams(amendedLeaseOption);
    trackEvent({
      trackingLabel: "terms-annual-mileage-selection",
      replaceEntities: [
        { ...leasingEntity, annual_mileage: Number(selectedValue || 0) },
      ],
    });
  };

  const handleContractLengthSelection = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedValue = event.target.value;
    const amendedLeaseOption = leaseTermParts
      ? constructLeaseOption({
          ...leaseTermParts,
          numberOfMonths: Number(selectedValue || 0),
        })
      : "";
    dispatch(setLeaseOption(amendedLeaseOption));
    handleQueryParams(amendedLeaseOption);
    trackEvent({
      trackingLabel: "terms-term-selection",
      replaceEntities: [{ ...leasingEntity, term: Number(selectedValue) }],
    });
  };

  const handleUpfrontPaymentSelection = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedValue = event.target.value;
    const amendedLeaseOption = leaseTermParts
      ? constructLeaseOption({
          ...leaseTermParts,
          depositMultiple: Number(selectedValue || 0),
        })
      : "";
    dispatch(setLeaseOption(amendedLeaseOption));
    handleQueryParams(amendedLeaseOption);
    trackEvent({
      trackingLabel: "terms-deposit-selection",
      replaceEntities: [{ ...leasingEntity, deposit: Number(selectedValue) }],
    });
  };

  const handleResetTerms = () => {
    handleQueryParams("cheapest");
    dispatch(setLeaseOption("cheapest"));
  };

  const mileageOptions: SelectOptions[] = MILEAGE_OPTIONS.map((mileage) => {
    return {
      displayValue: `${asFormattedString(mileage)} per year`,
      value: mileage,
    };
  });

  const contractLengthOptions: ToggleOptionProps[] = CONTRACT_LENGTH.map(
    (length) => {
      return {
        label: length,
        value: length,
        selected: Number(length) === leaseTermParts?.numberOfMonths,
      };
    },
  );

  const upfrontPaymentOptions: ToggleOptionProps[] =
    INITIAL_PAYMENT_MULTIPLIER.map((payment) => {
      return {
        label: payment,
        value: payment,
        selected: Number(payment) === leaseTermParts?.depositMultiple,
      };
    });

  const contractLengthHelpText = `${
    leaseTermParts?.numberOfMonths ?? "..."
  } months / ${
    calculateMonthToYear(leaseTermParts?.numberOfMonths) ?? "..."
  } years`;

  const calculatedDepositHelpText = `${
    leaseTermParts?.depositMultiple ?? "..."
  } ${leaseTermParts?.depositMultiple === 1 ? "month" : "months"} / ${`${
    deposit ?? "..."
  }`}`;

  return (
    <Styled.LeasingProductBordered
      id={"payment-customiser"}
      data-testid={"payment-customiser"}
      showError={!leaseOffersAvailable}
    >
      <TypeInsignia as="h2" weight="400">
        {leaseOffersAvailable
          ? "Customise your payment"
          : "Customise your terms"}
      </TypeInsignia>

      <Styled.LeasingProductCustomisePaymentFields>
        <Styled.LeasingProductFormField>
          <Select
            id="mileage"
            name="mileage"
            label="What's your expected annual mileage?"
            onChange={handleAnnualMileageSelection}
            options={mileageOptions}
            attributes={{
              value: leaseTermParts?.mileage,
              "aria-describedby": "mileage",
              [dataAttrTestId]: "mileage-select",
            }}
          />
        </Styled.LeasingProductFormField>
        <Styled.LeasingProductFormField>
          <Toggle
            name="leasing-product-months-toggle"
            id="leasing-product-contract-length-toggle"
            helpTextId="calculated-contract-length-help-text"
            legendText={`How long would you like the ${channelSingular} for? (months)`}
            helpText={contractLengthHelpText}
            onInteraction={handleContractLengthSelection}
            options={contractLengthOptions}
            attributes={{
              [dataAttrTestId]: "contract-length-toggle",
            }}
          />
        </Styled.LeasingProductFormField>
        <Styled.LeasingProductFormField>
          <Toggle
            name="leasing-product-upfront-toggle"
            legendText="How much would you like to pay upfront?"
            helpText={calculatedDepositHelpText}
            helpTextId="calculated-deposit-help-text"
            onInteraction={handleUpfrontPaymentSelection}
            options={upfrontPaymentOptions}
            attributes={{
              [dataAttrTestId]: "contract-upfront-toggle",
            }}
          />
        </Styled.LeasingProductFormField>
      </Styled.LeasingProductCustomisePaymentFields>
      <Styled.TermFilterReset
        type="button"
        displayType="tertiary"
        text="Reset to lowest monthly price"
        iconSrc={<IconReset title="" size={18} />}
        iconPosition={IconPosition.RIGHT}
        underline
        onInteraction={() => {
          handleResetTerms();
          trackEvent({ trackingLabel: "reset-filters" });
        }}
        attributes={{ [dataAttrGUI]: "cheapest-price-reset-terms" }}
      ></Styled.TermFilterReset>
      {!leaseOffersAvailable && (
        <Banner variation="alert">
          The terms selected are unavailable for this vehicle. Please select
          different terms.
        </Banner>
      )}
    </Styled.LeasingProductBordered>
  );
};
